import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths } from 'date-fns';
import { AlertCircle, Info, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import {
  reportsService,
  TopDrugStat,
  ControlledSubstanceStat,
  GeographicStat,
  FulfillmentRateStat,
  SeasonalTrend,
  DrugForecast,
} from '../../services/reports';
import { getApiError } from '@/services/api';
import { EmptyState, LoadingState, PageHeader } from '@/components/ui';
import {
  BarChart,
  ChartCard,
  DataTable,
  DateRangePicker,
  ExportButton,
  MultiLineChart,
  StackedBarChart,
  CHART_COLORS,
  formatPeriod,
  localeFor,
} from '@/components/reports';

type TabType = 'trends' | 'controlled' | 'geographic' | 'fulfillment' | 'seasonal' | 'forecast';
type GroupBy = 'day' | 'week' | 'month';

const TABS: TabType[] = ['trends', 'controlled', 'geographic', 'fulfillment', 'seasonal', 'forecast'];

// Report types accepted by GET /reports/government/export (see reports.schemas.ts).
const EXPORT_TYPES: Partial<Record<TabType, string>> = {
  trends: 'drug-trends',
  controlled: 'controlled',
  geographic: 'geographic',
  fulfillment: 'fulfillment-rate',
};

const num = (value: number | null | undefined, locale: string) => (value ?? 0).toLocaleString(locale);

function GroupByControl({ value, onChange }: { value: GroupBy; onChange: (v: GroupBy) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-500">{t('reports.groupBy')}</span>
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm" role="group">
        {(['day', 'week', 'month'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={value === option}
            className={clsx(
              'rounded-md px-3 py-1 text-sm font-medium transition-colors',
              value === option ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            {t(`reports.${option}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Shows loading / error states for a tab's query, otherwise its content. */
function TabState({
  isLoading,
  error,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="card">
        <LoadingState />
      </div>
    );
  }
  if (error) {
    return (
      <div className="card">
        <EmptyState icon={AlertCircle} title={t('admin.loadError')} description={getApiError(error).message} />
      </div>
    );
  }
  return <>{children}</>;
}

export function GovernmentReports() {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  const [activeTab, setActiveTab] = useState<TabType>('trends');
  const [dateRange, setDateRange] = useState({
    fromDate: format(subMonths(new Date(), 3), 'yyyy-MM-dd'),
    toDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [groupBy, setGroupBy] = useState<GroupBy>('month');
  const rangeValid = dateRange.fromDate <= dateRange.toDate;

  const drugTrends = useQuery({
    queryKey: ['gov-drug-trends', dateRange, groupBy],
    queryFn: () => reportsService.getGovDrugTrends({ ...dateRange, groupBy }),
    enabled: activeTab === 'trends' && rangeValid,
  });

  const topDrugsByATC = useQuery({
    queryKey: ['gov-top-drugs-atc', dateRange],
    queryFn: () => reportsService.getGovTopDrugsByATC({ ...dateRange, limit: 10 }),
    enabled: activeTab === 'trends' && rangeValid,
  });

  const controlled = useQuery({
    queryKey: ['gov-controlled', dateRange],
    queryFn: () => reportsService.getGovControlledSubstances(dateRange),
    enabled: activeTab === 'controlled' && rangeValid,
  });

  const geographic = useQuery({
    queryKey: ['gov-geographic', dateRange],
    queryFn: () => reportsService.getGovGeographic(dateRange),
    enabled: activeTab === 'geographic' && rangeValid,
  });

  const fulfillment = useQuery({
    queryKey: ['gov-fulfillment-rate', dateRange, groupBy],
    queryFn: () => reportsService.getGovFulfillmentRate({ ...dateRange, groupBy }),
    enabled: activeTab === 'fulfillment' && rangeValid,
  });

  const seasonal = useQuery({
    queryKey: ['gov-seasonal', dateRange],
    queryFn: () => reportsService.getGovSeasonalTrends(dateRange),
    enabled: activeTab === 'seasonal' && rangeValid,
  });

  const forecast = useQuery({
    queryKey: ['gov-forecast'],
    queryFn: () => reportsService.getGovForecast({ months: 3 }),
    enabled: activeTab === 'forecast',
  });

  const exportType = EXPORT_TYPES[activeTab];
  const monthName = (month: number, style: 'short' | 'long') =>
    month >= 1 && month <= 12 ? new Date(2000, month - 1, 1).toLocaleDateString(locale, { month: style }) : String(month);
  const rateClass = (rate: number) =>
    rate >= 80 ? 'text-success-700' : rate >= 50 ? 'text-warning-700' : 'text-danger-700';

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin.gov.title')}
        description={t('admin.gov.description')}
        actions={
          exportType && (
            <ExportButton
              reportType={exportType}
              fromDate={dateRange.fromDate}
              toDate={dateRange.toDate}
              disabled={!rangeValid}
            />
          )
        }
      />

      {activeTab !== 'forecast' && (
        <DateRangePicker
          fromDate={dateRange.fromDate}
          toDate={dateRange.toDate}
          onChange={(from, to) => setDateRange({ fromDate: from, toDate: to })}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="scrollbar-hide -mb-px flex gap-6 overflow-x-auto" role="tablist" aria-label={t('admin.gov.title')}>
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={clsx(
                'shrink-0 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              {t(`admin.gov.tabs.${tab}`)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'trends' && (
        <div className="space-y-6">
          <GroupByControl value={groupBy} onChange={setGroupBy} />
          <TabState isLoading={drugTrends.isLoading} error={drugTrends.error}>
            <MultiLineChart
              data={drugTrends.data || []}
              title={t('admin.gov.trendsChart')}
              height={400}
              groupBy={groupBy}
            />
          </TabState>

          <TabState isLoading={topDrugsByATC.isLoading} error={topDrugsByATC.error}>
            {topDrugsByATC.data && topDrugsByATC.data.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {topDrugsByATC.data.slice(0, 4).map((atcGroup) => (
                  <DataTable<TopDrugStat>
                    key={atcGroup.atcClass}
                    title={t('admin.gov.topDrugsAtc', { atc: atcGroup.atcClass })}
                    data={atcGroup.drugs}
                    rowKey={(row) => row.drugId}
                    columns={[
                      { key: 'tradeName', header: t('admin.gov.cols.drug') },
                      { key: 'dci', header: t('admin.gov.cols.dci') },
                      {
                        key: 'totalQuantity',
                        header: t('admin.gov.cols.qty'),
                        className: 'text-end',
                        render: (row) => num(row.totalQuantity, locale),
                      },
                    ]}
                    maxRows={5}
                  />
                ))}
              </div>
            ) : (
              <ChartCard title={t('admin.gov.topDrugsTitle')} isEmpty />
            )}
          </TabState>
        </div>
      )}

      {activeTab === 'controlled' && (
        <TabState isLoading={controlled.isLoading} error={controlled.error}>
          <div className="space-y-6">
            <BarChart
              data={(controlled.data ?? []).map((s) => ({
                name: t(`drug.tableau${s.controlledClass}`, { defaultValue: s.controlledClass }),
                value: s.totalPrescribed,
              }))}
              title={t('admin.gov.controlledChart')}
              valueLabel={t('admin.gov.cols.totalPrescribed')}
              color={CHART_COLORS.navy}
              horizontal
            />
            <DataTable<ControlledSubstanceStat>
              title={t('admin.gov.controlledTable')}
              data={controlled.data ?? []}
              rowKey={(row) => row.controlledClass}
              columns={[
                {
                  key: 'controlledClass',
                  header: t('admin.gov.cols.class'),
                  render: (row) => t(`drug.tableau${row.controlledClass}`, { defaultValue: row.controlledClass }),
                },
                { key: 'drugCount', header: t('admin.gov.cols.drugs'), className: 'text-end', render: (row) => num(row.drugCount, locale) },
                {
                  key: 'totalPrescribed',
                  header: t('admin.gov.cols.totalPrescribed'),
                  className: 'text-end',
                  render: (row) => num(row.totalPrescribed, locale),
                },
                {
                  key: 'totalDispensed',
                  header: t('admin.gov.cols.totalDispensed'),
                  className: 'text-end',
                  render: (row) => num(row.totalDispensed, locale),
                },
                {
                  key: 'rate',
                  header: t('admin.gov.cols.dispensingRate'),
                  className: 'text-end',
                  render: (row) =>
                    row.totalPrescribed > 0
                      ? `${Math.round(((row.totalDispensed ?? 0) / row.totalPrescribed) * 100)}%`
                      : '—',
                },
              ]}
            />
          </div>
        </TabState>
      )}

      {activeTab === 'geographic' && (
        <TabState isLoading={geographic.isLoading} error={geographic.error}>
          <div className="space-y-6">
            <BarChart
              data={(geographic.data ?? []).slice(0, 20).map((s) => ({
                name: s.wilayaName,
                value: s.prescriptionCount,
              }))}
              title={t('admin.gov.geoChart')}
              valueLabel={t('admin.gov.cols.prescriptions')}
              horizontal
              height={Math.max(240, Math.min(20, geographic.data?.length ?? 0) * 26)}
            />
            <DataTable<GeographicStat>
              title={t('admin.gov.geoTable')}
              data={geographic.data ?? []}
              rowKey={(row) => String(row.wilayaId)}
              columns={[
                { key: 'wilayaName', header: t('admin.gov.cols.wilaya') },
                {
                  key: 'prescriptionCount',
                  header: t('admin.gov.cols.prescriptions'),
                  className: 'text-end',
                  render: (row) => num(row.prescriptionCount, locale),
                },
                {
                  key: 'fulfillmentCount',
                  header: t('admin.gov.cols.fulfillments'),
                  className: 'text-end',
                  render: (row) => num(row.fulfillmentCount, locale),
                },
                {
                  key: 'fulfillmentRate',
                  header: t('admin.gov.cols.fulfillmentRate'),
                  className: 'text-end',
                  render: (row) => (
                    <span className={clsx('font-medium tabular-nums', rateClass(row.fulfillmentRate ?? 0))}>
                      {row.fulfillmentRate ?? 0}%
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </TabState>
      )}

      {activeTab === 'fulfillment' && (
        <div className="space-y-6">
          <GroupByControl value={groupBy} onChange={setGroupBy} />
          <TabState isLoading={fulfillment.isLoading} error={fulfillment.error}>
            <div className="space-y-6">
              <StackedBarChart
                data={(fulfillment.data ?? []).map((r) => ({
                  period: r.period,
                  fulfilled: r.fulfilledPrescriptions ?? 0,
                  unfulfilled: Math.max(0, (r.totalPrescriptions ?? 0) - (r.fulfilledPrescriptions ?? 0)),
                }))}
                keys={[
                  { key: 'fulfilled', name: t('admin.gov.fulfilled'), color: CHART_COLORS.primary },
                  { key: 'unfulfilled', name: t('admin.gov.unfulfilled'), color: '#cbd5e1' },
                ]}
                xAxisKey="period"
                labelFormatter={(v) => formatPeriod(v, locale, false, groupBy)}
                title={t('admin.gov.fulfillmentChart')}
              />
              <DataTable<FulfillmentRateStat>
                title={t('admin.gov.fulfillmentTable')}
                data={fulfillment.data ?? []}
                rowKey={(row) => row.period}
                columns={[
                  {
                    key: 'period',
                    header: t('admin.gov.cols.period'),
                    render: (row) => formatPeriod(row.period, locale, true, groupBy),
                  },
                  {
                    key: 'totalPrescriptions',
                    header: t('admin.gov.cols.totalRx'),
                    className: 'text-end',
                    render: (row) => num(row.totalPrescriptions, locale),
                  },
                  {
                    key: 'fulfilledPrescriptions',
                    header: t('admin.gov.fulfilled'),
                    className: 'text-end',
                    render: (row) => num(row.fulfilledPrescriptions, locale),
                  },
                  {
                    key: 'fulfillmentRate',
                    header: t('admin.gov.cols.rate'),
                    className: 'text-end',
                    render: (row) => `${row.fulfillmentRate ?? 0}%`,
                  },
                  {
                    key: 'averageTimeToFulfillmentHours',
                    header: t('admin.gov.cols.avgHours'),
                    className: 'text-end',
                    render: (row) =>
                      (row.averageTimeToFulfillmentHours ?? 0).toLocaleString(locale, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      }),
                  },
                ]}
              />
            </div>
          </TabState>
        </div>
      )}

      {activeTab === 'seasonal' && (
        <TabState isLoading={seasonal.isLoading} error={seasonal.error}>
          <div className="space-y-6">
            <BarChart
              data={(seasonal.data ?? []).map((s) => ({
                name: monthName(s.month, 'short'),
                value: s.averagePrescriptions,
              }))}
              title={t('admin.gov.seasonalChart')}
              valueLabel={t('admin.gov.cols.avgPrescriptions')}
            />
            <DataTable<SeasonalTrend>
              title={t('admin.gov.seasonalTable')}
              data={seasonal.data ?? []}
              rowKey={(row) => String(row.month)}
              columns={[
                {
                  key: 'monthName',
                  header: t('admin.gov.cols.month'),
                  render: (row) => <span className="capitalize">{monthName(row.month, 'long')}</span>,
                },
                {
                  key: 'averagePrescriptions',
                  header: t('admin.gov.cols.avgPrescriptions'),
                  className: 'text-end',
                  render: (row) => num(row.averagePrescriptions, locale),
                },
                {
                  key: 'topDrugs',
                  header: t('admin.gov.cols.topDrugs'),
                  render: (row) => (
                    <span className="block max-w-md truncate" title={row.topDrugs?.join(', ')}>
                      {row.topDrugs?.length ? row.topDrugs.join(', ') : '—'}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </TabState>
      )}

      {activeTab === 'forecast' && (
        <div className="space-y-6">
          <div className="alert-info">
            <Info className="h-5 w-5 shrink-0" />
            <div>
              <p className="font-medium">{t('admin.gov.forecastTitle')}</p>
              <p className="mt-0.5">{t('admin.gov.forecastHint')}</p>
            </div>
          </div>

          <TabState isLoading={forecast.isLoading} error={forecast.error}>
            <DataTable<DrugForecast>
              title={t('admin.gov.forecastTable')}
              data={forecast.data ?? []}
              rowKey={(row) => row.drugId}
              columns={[
                { key: 'tradeName', header: t('admin.gov.cols.drug') },
                { key: 'dci', header: t('admin.gov.cols.dci') },
                {
                  key: 'atcCode',
                  header: t('admin.gov.cols.atc'),
                  render: (row) => (row.atcCode ? <span className="font-mono text-xs">{row.atcCode}</span> : '—'),
                },
                {
                  key: 'forecastedQuantity',
                  header: t('admin.gov.cols.forecastQty'),
                  className: 'text-end',
                  render: (row) => num(row.forecastedQuantity, locale),
                },
                {
                  key: 'trend',
                  header: t('admin.gov.cols.trend'),
                  render: (row) => {
                    const Icon =
                      row.trend === 'increasing' ? TrendingUp : row.trend === 'decreasing' ? TrendingDown : Minus;
                    return (
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 text-sm font-medium',
                          row.trend === 'increasing'
                            ? 'text-success-700'
                            : row.trend === 'decreasing'
                              ? 'text-danger-700'
                              : 'text-gray-500'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {t(`admin.gov.trend.${row.trend}`, { defaultValue: row.trend })}
                      </span>
                    );
                  },
                },
                {
                  key: 'confidence',
                  header: t('admin.gov.cols.confidence'),
                  render: (row) => {
                    const pct = Math.round(Math.min(1, Math.max(0, row.confidence ?? 0)) * 100);
                    return (
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className={clsx(
                              'h-full rounded-full',
                              pct >= 70 ? 'bg-success-500' : pct >= 50 ? 'bg-warning-500' : 'bg-danger-500'
                            )}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs tabular-nums text-gray-600">{pct}%</span>
                      </div>
                    );
                  },
                },
              ]}
            />
          </TabState>
        </div>
      )}
    </div>
  );
}
